import { Button, MenuItem, Typography } from '@mui/material';
import React, { FormEventHandler, useEffect } from 'react';
import {
  reduxForm,
  Field,
  getFormValues,
  getFormSyncErrors,
  initialize,
} from 'redux-form/immutable';
import { useDispatch, useSelector } from 'react-redux';
import LinearProgress from '@material-ui/core/LinearProgress';
import { Grid } from '@material-ui/core';
import type { InjectedFormProps } from 'redux-form';
import _ from 'lodash';

import FormSelectRedesign from 'retired/FormSelectRedesign';
import Box from 'retired/Box';
import { selectOrganization } from 'entities/user/selectors';
import type { State } from 'reducers';
import type { TypedMap } from 'utils/typed-map';
import { showErrorAction } from 'containers/Notification/actions';
import Organization from 'entities/organization/model';

import * as Actions from './actions';

const FORM_IDENTIFIER = 'app/OrganizationPage/NonprofitVerificationStatus';

type FormData = Pick<Organization, 'verificationStatus'>;

const NonprofitVerificationStatus = ({ submitting, initialized }: InjectedFormProps) => {
  const dispatch = useDispatch();
  const organization = useSelector((state: State) => selectOrganization(state, true));
  const syncErrors = useSelector(getFormSyncErrors(FORM_IDENTIFIER));
  const registeredFields = useSelector((state: State) =>
    state.getIn(['form', FORM_IDENTIFIER, 'registeredFields'])
  );

  useEffect(() => {
    const initialValues = organization?.toJS() || {};
    if (!initialValues?.verificationStatus) {
      initialValues.verificationStatus = '-';
    }

    dispatch(initialize(FORM_IDENTIFIER, initialValues));
  }, []);

  const currentFormValues = useSelector(getFormValues(FORM_IDENTIFIER)) as TypedMap<FormData>;

  if (!organization || !initialized) {
    return <LinearProgress />;
  }

  const submit = (data = currentFormValues.toJS()) => {
    const firstSyncError = Object.values(syncErrors)[0];

    if (firstSyncError) {
      dispatch(showErrorAction(firstSyncError));
    } else {
      const formKeys = registeredFields ? [...registeredFields?.keys()] : [];
      dispatch(Actions.saveAction(_.pick(data, ['id', ...formKeys]), FORM_IDENTIFIER));
    }
  };

  const handleFormEvent: FormEventHandler = (event) => {
    event.preventDefault();
    submit();
  };

  return (
    // eslint-disable-next-line react/no-unknown-property
    <form noValidate onSubmitCapture={handleFormEvent}>
      <fieldset>
        <Box style={{ paddingTop: 0, marginBottom: 30 }}>
          <h2>Nonprofit Verification Status</h2>
          <Grid container spacing={3}>
            <Grid item sm={12} md={6} lg={8}>
              <Field name="verificationStatus" component={FormSelectRedesign} label="Status">
                <MenuItem value="-">Not set</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
              </Field>
            </Grid>
          </Grid>

          <Typography align="right">
            <Button type="submit" variant="contained" color="primary" disabled={submitting}>
              Submit
            </Button>
          </Typography>
        </Box>
      </fieldset>
    </form>
  );
};

const withForm = reduxForm({
  form: FORM_IDENTIFIER,
  destroyOnUnmount: false,
  enableReinitialize: true,
  keepDirtyOnReinitialize: true,
});

export default withForm(NonprofitVerificationStatus);
